import type { ButtonHTMLAttributes, MouseEvent, Ref } from 'react';

import './botao.css';

/** As quatro intenções que já existem na casca e em Ajustes, nada além:
 *  `primario` é a ação da tela, `secundario` a alternativa, `texto` o link
 *  que precisa de alvo de toque e `perigo` o que não se desfaz. */
export type VarianteBotao = 'primario' | 'secundario' | 'texto' | 'perigo';

export type PropsBotao = {
  variante?: VarianteBotao;
  largura?: 'auto' | 'cheia';
  /** O botão está trabalhando **agora**, por causa do toque que acabou de
   *  receber: anuncia `aria-busy`, marca `aria-disabled` e ignora os toques
   *  seguintes — mas **nunca** desabilita de verdade.
   *
   *  Um botão que ganha `disabled` perde o foco e sai do rotor do
   *  VoiceOver, e isso acontece justamente no controle que a pessoa acabou
   *  de apertar: quem espera resposta é largado no meio da tela e o
   *  `aria-busy` que o botão acabou de ganhar não chega a ser anunciado,
   *  porque não há mais elemento no rotor para anunciá-lo. A trava do toque
   *  repetido é o retorno antecipado do `onClick` daqui.
   *
   *  É diferente de `disabled`, que continua sendo `disabled` nativo: ali o
   *  significado é "não disponível", e um controle indisponível sair do
   *  rotor é o comportamento certo. */
  carregando?: boolean;
  /** Quem precisa **mover o foco** para este botão. É o caso da tela
   *  trancada, que aparece por cima de conteúdo vivo: sem mover o foco, o
   *  elemento focado some do DOM, o cursor do VoiceOver cai no `<body>` e
   *  nada anuncia que o app trancou. React 19 passa `ref` como prop comum
   *  em componente de função; aqui ele viaja no espalhamento até o
   *  `<button>`. */
  ref?: Ref<HTMLButtonElement>;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export function Botao({
  variante = 'primario',
  largura = 'auto',
  carregando = false,
  // O padrão do HTML dentro de um `form` é `submit`: mais de um botão numa
  // tela de Ajustes enviaria o formulário sem querer.
  type = 'button',
  disabled = false,
  className,
  onClick,
  children,
  ...resto
}: PropsBotao) {
  return (
    <button
      type={type}
      data-variante={variante}
      data-largura={largura}
      className={className ? `ds-botao ${className}` : 'ds-botao'}
      disabled={disabled}
      // `false` viraria `aria-busy="false"`, que é ruído no leitor de tela.
      aria-busy={carregando ? true : undefined}
      // Indisponível para quem ouve, focável para quem navega: é o par certo
      // de `aria-busy`. Quem quiser marcar os **vizinhos** como
      // indisponíveis passa o seu próprio `aria-disabled` — como vem depois
      // no espalhamento, ele vence este.
      aria-disabled={carregando ? true : undefined}
      onClick={(evento: MouseEvent<HTMLButtonElement>) => {
        // Sem `disabled` nativo, o clique chega: a trava é aqui. Vale também
        // para o teclado, que ativa o botão pelo mesmo evento.
        if (carregando) {
          evento.preventDefault();
          return;
        }
        onClick?.(evento);
      }}
      {...resto}
    >
      {children}
      {/* O estado de espera não vai só na cor nem só no `aria-busy`: há um
          indicador visual, escondido do leitor de tela porque quem o anuncia
          é o próprio `aria-busy`. */}
      {carregando ? (
        <span className="ds-botao__giro" aria-hidden="true" />
      ) : null}
    </button>
  );
}
